import { appInsightsService } from "./appInsights.service";
import { v4 as uuidv4 } from "uuid";

/**
 * Log levels for the application
 */
export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

/**
 * Logger configuration options
 */
export interface LoggerConfig {
  enableConsole?: boolean;
  enableAppInsights?: boolean;
  minLogLevel?: LogLevel;
}

/**
 * Base context that gets automatically added to every log
 */
interface BaseContext {
  timestamp: string;
  correlationId?: string;
  environment: string;
  userId?: string;
  tenantId?: string;
  instanceId?: string;
  sessionId?: string;
  component?: string;
  level: LogLevel;
}

/**
 * Custom context that can be added to specific logs
 */
export interface LogContext {
  [key: string]: unknown;
}

/**
 * Operation tracking for timed operations
 */
interface Operation {
  name: string;
  correlationId: string;
  startTime: number;
}

/**
 * Centralized Logger Service
 *
 * Features:
 * - Singleton pattern for consistent usage across the app
 * - 4 log levels: debug, info, warn, error
 * - Integration with Application Insights for production monitoring
 * - Correlation ID generation and tracking for request tracing
 * - Automatic context enrichment (user, tenant, environment, etc.)
 * - Environment-aware behavior (console in dev, App Insights in prod)
 * - Structured logging format for better querying
 */
class LoggerService {
  private static instance: LoggerService;
  private sessionId: string;
  private instanceId: string;
  private operations: Map<string, Operation> = new Map();
  private currentCorrelationId?: string;
  private config: LoggerConfig;

  private constructor() {
    this.sessionId = uuidv4();
    this.instanceId = uuidv4();

    // Initialize with default config from environment variables
    this.config = {
      enableConsole: this.getDefaultConsoleEnabled(),
      enableAppInsights: this.getDefaultAppInsightsEnabled(),
      minLogLevel: LogLevel.DEBUG,
    };
  }

  /**
   * Get default console enabled state from environment
   */
  private getDefaultConsoleEnabled(): boolean {
    // Check environment variable first
    const envValue = import.meta.env.VITE_LOGGER_ENABLE_CONSOLE;
    if (envValue !== undefined) {
      return envValue === "true";
    }
    // Default: enabled in development, warnings/errors only in production
    return true;
  }

  /**
   * Get default Application Insights enabled state from environment
   */
  private getDefaultAppInsightsEnabled(): boolean {
    // Check environment variable first
    const envValue = import.meta.env.VITE_LOGGER_ENABLE_APP_INSIGHTS;
    if (envValue !== undefined) {
      return envValue === "true";
    }
    // Default: always enabled (will respect App Insights' own disable flag)
    return true;
  }

  /**
   * Update logger configuration at runtime
   */
  public configure(config: Partial<LoggerConfig>): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  /**
   * Get current logger configuration
   */
  public getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  /**
   * Get current environment
   */
  private getEnvironment(): string {
    // Use Vite's built-in MODE (development, production, staging, etc.)
    // Or fall back to VITE_APP_ENV if set
    return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "production";
  }

  /**
   * Get numeric value for log level (for comparison)
   */
  private getLogLevelValue(level: LogLevel): number {
    const levels = {
      [LogLevel.DEBUG]: 0,
      [LogLevel.INFO]: 1,
      [LogLevel.WARN]: 2,
      [LogLevel.ERROR]: 3,
    };
    return levels[level];
  }

  /**
   * Check if log level meets minimum threshold
   */
  private shouldLog(level: LogLevel): boolean {
    if (!this.config.minLogLevel) return true;
    return this.getLogLevelValue(level) >= this.getLogLevelValue(this.config.minLogLevel);
  }

  /**
   * Check if we should log to console based on configuration and environment
   */
  private shouldLogToConsole(level: LogLevel): boolean {
    // Check if console logging is enabled
    if (!this.config.enableConsole) return false;

    // Check if log level meets threshold
    if (!this.shouldLog(level)) return false;

    // In development, log everything
    if (import.meta.env.DEV) return true;

    // In production, only log warnings and errors to console by default
    const env = this.getEnvironment();
    if (env === "production") {
      return level === LogLevel.WARN || level === LogLevel.ERROR;
    }

    // In staging, log info, warn, and error
    return level !== LogLevel.DEBUG;
  }

  /**
   * Check if we should log to Application Insights
   */
  private shouldLogToAppInsights(level: LogLevel): boolean {
    // Check if App Insights logging is enabled
    if (!this.config.enableAppInsights) return false;

    // Check if log level meets threshold
    return this.shouldLog(level);
  }

  /**
   * Get base context that's added to every log
   */
  private getBaseContext(level: LogLevel, component?: string): BaseContext {
    const context: BaseContext = {
      timestamp: new Date().toISOString(),
      environment: this.getEnvironment(),
      sessionId: this.sessionId,
      instanceId: this.instanceId,
      level,
    };

    // Add correlation ID if available
    if (this.currentCorrelationId) {
      context.correlationId = this.currentCorrelationId;
    }

    // Add component if provided
    if (component) {
      context.component = component;
    }

    // Try to get user context from local storage or auth service
    try {
      const userInfo = localStorage.getItem("userInfo");
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        if (parsed.userId) context.userId = parsed.userId;
        if (parsed.tenantId) context.tenantId = parsed.tenantId;
      }
    } catch {
      // Ignore errors getting user context
    }

    return context;
  }

  /**
   * Format log message for console output with colors
   */
  private formatConsoleMessage(level: LogLevel, message: string): [string, string, string] {
    const colors = {
      [LogLevel.DEBUG]: "color: #6c757d",
      [LogLevel.INFO]: "color: #0dcaf0",
      [LogLevel.WARN]: "color: #ffc107",
      [LogLevel.ERROR]: "color: #dc3545",
    };

    const icons = {
      [LogLevel.DEBUG]: "🔍",
      [LogLevel.INFO]: "ℹ️",
      [LogLevel.WARN]: "⚠️",
      [LogLevel.ERROR]: "❌",
    };

    const timestamp = new Date().toISOString();
    const icon = icons[level];
    const color = colors[level];

    return [`%c${icon} [${level.toUpperCase()}] ${timestamp}`, color, message];
  }

  /**
   * Send log to Application Insights
   */
  private sendToAppInsights(
    level: LogLevel,
    message: string,
    error?: Error,
    customContext?: LogContext,
  ): void {
    // Check if we should log to App Insights
    if (!this.shouldLogToAppInsights(level)) return;

    const baseContext = this.getBaseContext(level);
    const fullContext = { ...baseContext, ...customContext };

    // Track errors as exceptions
    if (level === LogLevel.ERROR && error) {
      appInsightsService.trackException(error, {
        message,
        ...fullContext,
      });
    } else {
      // Track other log levels as events
      appInsightsService.trackEvent(`Log_${level}`, {
        message,
        ...fullContext,
      });
    }
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, error?: Error, context?: LogContext): void {
    // Log to console if appropriate
    if (this.shouldLogToConsole(level)) {
      const [formatted, color, msg] = this.formatConsoleMessage(level, message);

      switch (level) {
        case LogLevel.DEBUG:
          console.log(formatted, color, msg);
          if (context) console.log("Context:", context);
          break;
        case LogLevel.INFO:
          console.info(formatted, color, msg);
          if (context) console.info("Context:", context);
          break;
        case LogLevel.WARN:
          console.warn(formatted, color, msg);
          if (context) console.warn("Context:", context);
          break;
        case LogLevel.ERROR:
          console.error(formatted, color, msg);
          if (error) console.error("Error:", error);
          if (context) console.error("Context:", context);
          break;
      }
    }

    // Send to Application Insights if enabled
    this.sendToAppInsights(level, message, error, context);
  }

  /**
   * Log debug message (development/troubleshooting only)
   */
  public debug(message: string, context?: LogContext): void {
    this.log(LogLevel.DEBUG, message, undefined, context);
  }

  /**
   * Log info message (important business events)
   */
  public info(message: string, context?: LogContext): void {
    this.log(LogLevel.INFO, message, undefined, context);
  }

  /**
   * Log warning message (recoverable errors)
   */
  public warn(message: string, context?: LogContext): void {
    this.log(LogLevel.WARN, message, undefined, context);
  }

  /**
   * Log error message (unhandled exceptions, API failures)
   */
  public error(message: string, error?: Error | unknown, context?: LogContext): void {
    // Handle unknown error types
    let errorObj: Error | undefined;
    if (error instanceof Error) {
      errorObj = error;
    } else if (error) {
      // Convert unknown error to Error object
      errorObj = new Error(String(error));
    }

    this.log(LogLevel.ERROR, message, errorObj, context);
  }

  /**
   * Generate and set a new correlation ID
   */
  public generateCorrelationId(): string {
    const correlationId = uuidv4();
    this.currentCorrelationId = correlationId;
    return correlationId;
  }

  /**
   * Set existing correlation ID (e.g., from HTTP headers)
   */
  public setCorrelationId(correlationId: string): void {
    this.currentCorrelationId = correlationId;
  }

  /**
   * Get current correlation ID
   */
  public getCorrelationId(): string | undefined {
    return this.currentCorrelationId;
  }

  /**
   * Clear correlation ID
   */
  public clearCorrelationId(): void {
    this.currentCorrelationId = undefined;
  }

  /**
   * Start tracking a timed operation
   * Returns a correlation ID that should be passed to endOperation
   */
  public startOperation(name: string, context?: LogContext): string {
    const correlationId = this.generateCorrelationId();
    const operation: Operation = {
      name,
      correlationId,
      startTime: Date.now(),
    };

    this.operations.set(correlationId, operation);
    this.info(`Operation started: ${name}`, { ...context, correlationId });

    // Also track in Application Insights
    appInsightsService.startTrackEvent(name);

    return correlationId;
  }

  /**
   * End tracking a timed operation
   */
  public endOperation(correlationId: string, success: boolean, context?: LogContext): void {
    const operation = this.operations.get(correlationId);
    if (!operation) {
      this.warn("Attempted to end unknown operation", { correlationId });
      return;
    }

    const duration = Date.now() - operation.startTime;
    const status = success ? "succeeded" : "failed";

    // Stop Application Insights tracking
    appInsightsService.stopTrackEvent(operation.name, {
      success,
      duration,
      correlationId,
      ...context,
    });

    // Log completion
    const logMethod = success ? this.info.bind(this) : this.error.bind(this);
    logMethod(`Operation ${status}: ${operation.name}`, undefined, {
      duration,
      correlationId,
      ...context,
    });

    // Clean up
    this.operations.delete(correlationId);
    this.clearCorrelationId();
  }

  /**
   * Get session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get instance ID
   */
  public getInstanceId(): string {
    return this.instanceId;
  }
}

// Export singleton instance
export const logger = LoggerService.getInstance();
